import { Component } from "@theme/component";
import { ResizeNotifier } from "@theme/utilities";

/** Must match the clamp on the copy element in `snippets/read-more-text.liquid`. */
const CLAMP_CLASS = "line-clamp-2";
const LINES = 2;

/**
 * @typedef {object} Refs
 * @property {HTMLElement} copy Plain text, truncated to fit alongside the toggle.
 * @property {HTMLElement} full The original formatted content, shown when expanded.
 * @property {HTMLButtonElement} toggle
 */

/**
 * @extends {Component<Refs>}
 */
export default class ReadMoreText extends Component {
  requiredRefs = ["copy", "full", "toggle"];

  connectedCallback() {
    super.connectedCallback();

    this.#reset();
    this.#resizeObserver.observe(this);
    this.#copyObserver.observe(this, { childList: true, characterData: true, subtree: true });
    this.#clampWhenFontsReady();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver.disconnect();
    this.#copyObserver.disconnect();
  }

  get expanded() {
    return this.dataset.expanded === "true";
  }

  set expanded(value) {
    this.dataset.expanded = value ? "true" : "false";
  }

  toggleExpanded() {
    this.#setUserExpanded(!this.#userExpanded);
    this.expanded = this.#userExpanded;
  }

  /**
   * Kept apart from `expanded`, which also gets set while measuring and when the content is short
   * enough to be shown in full without a toggle.
   */
  #userExpanded = false;

  /**
   * Mirrored to the DOM so ancestors can style against a real user expansion, which
   * `data-expanded` can't express.
   * @param {boolean} value
   */
  #setUserExpanded(value) {
    this.#userExpanded = value;
    this.dataset.userExpanded = value ? "true" : "false";
  }

  /** @type {string} */
  #fullCopy = "";

  /** @type {string} */
  #clampedCopy = "";

  #width = 0;

  #reset() {
    this.#fullCopy = this.refs.copy.textContent.trim();
    this.#setUserExpanded(false);
    this.#clamp();
  }

  /**
   * Fonts load with `font-display: swap`, so the first measurement can happen with fallback
   * metrics. The swap only changes the height, which the resize handler ignores.
   */
  #clampWhenFontsReady() {
    if (document.fonts.status === "loaded") return;

    document.fonts.ready.then(() => {
      if (this.isConnected) this.#clamp();
    });
  }

  #clamp() {
    const { copy, toggle } = this.refs;
    const lineHeight = parseFloat(getComputedStyle(this).lineHeight);

    if (!Number.isFinite(lineHeight)) return;

    copy.classList.remove(CLAMP_CLASS);

    this.#width = this.offsetWidth;

    const maxHeight = lineHeight * LINES + 1;

    // Measure the formatted content, since that is what shows when no toggle is needed.
    toggle.hidden = true;
    this.expanded = true;

    if (this.offsetHeight <= maxHeight) {
      this.#clampedCopy = this.#fullCopy;
      this.#render();
      return;
    }

    // The toggle sits inline after the teaser, so it has to take up room while measuring.
    this.expanded = false;
    toggle.hidden = false;

    const words = this.#fullCopy.split(/\s+/);
    let low = 0;
    let high = words.length;

    while (low < high) {
      const middle = Math.ceil((low + high) / 2);

      copy.textContent = this.#truncate(words.slice(0, middle));

      if (this.offsetHeight <= maxHeight) {
        low = middle;
      } else {
        high = middle - 1;
      }
    }

    this.#clampedCopy = this.#truncate(words.slice(0, low));
    this.expanded = this.#userExpanded;

    this.#render();
  }

  #render() {
    this.refs.copy.textContent = this.#clampedCopy;

    // Discard the records for our own writes so they don't look like new copy.
    this.#copyObserver.takeRecords();
  }

  /**
   * @param {string[]} words
   * @returns {string}
   */
  #truncate(words) {
    return `${words.join(" ").replace(/[\s.,;:!?-]+$/, "")}...`;
  }

  #handleResize = () => {
    if (this.offsetWidth === this.#width) return;

    this.#clamp();
  };

  #resizeObserver = new ResizeNotifier(this.#handleResize);

  /** The Section Rendering API replaces the copy with the full text when it re-renders. */
  #copyObserver = new MutationObserver(() => this.#reset());
}

if (!customElements.get("read-more-text")) {
  customElements.define("read-more-text", ReadMoreText);
}
